import { useMemo, useCallback } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { getSessionId, clearSession, hasSession } from "@/lib/session";

/**
 * Hook for managing booking session within tenant context
 *
 * Provides session ID retrieval and clearing functionality,
 * automatically scoped to the current tenant.
 */
export function useSession() {
  const { slug } = useTenant();

  /**
   * Get the current session ID for this tenant
   * Creates a new session if one doesn't exist
   */
  const sessionId = useMemo(() => {
    if (!slug) return null;
    return getSessionId(slug);
  }, [slug]);

  /**
   * Clear the current session (e.g., after booking completion or expiry)
   */
  const clearCurrentSession = useCallback(() => {
    if (slug) {
      clearSession(slug);
    }
  }, [slug]);

  /**
   * Check if a session currently exists
   */
  const sessionExists = useMemo(() => {
    if (!slug) return false;
    return hasSession(slug);
  }, [slug]);

  return {
    sessionId,
    clearSession: clearCurrentSession,
    hasSession: sessionExists,
    slug,
  };
}

export default useSession;
